import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "../features.css";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { useAuth } from "../../auth/AuthProvider";
import { ownSplitFor, useBookData } from "../../data/BookDataProvider";
import {
  COMMISSION_RATE,
  PREMIUM_SANITY_CAP,
  daysUntil,
  formatDate,
  formatMoney,
  parsePremium,
  renewalInfo,
  sumPremiums
} from "../../data/format";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  MiniTimeline,
  NudgeCard,
  SkeletonText,
  StatTile,
  useToast,
  ClockIcon,
  PlusIcon
} from "../../ui";
import { LobBadge, prospectStageInfo } from "../shared";

/**
 * Referral Home (/app/home) — REFERRAL-UX-SPEC §1. The page reads as one
 * sentence: here's your book → anyone worth a hello? → what's renewing →
 * where your referrals stand → refer someone new. A briefing, not a
 * workspace: single 760px column, questions not tasks, no red anywhere.
 */

const RADAR_WINDOW_DAYS = 60;
const RADAR_FALLBACK_DAYS = 90;
const NUDGES_VISIBLE = 3;

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) {
    return "Good morning";
  }
  if (hour < 17) {
    return "Good afternoon";
  }
  return "Good evening";
}

/** Gentle urgency (E §1.4): amber inside 14 days, never red. */
function radarVariant(days) {
  return days != null && days <= 14 ? "warning" : "neutral";
}

function relativeDate(days) {
  if (days == null) {
    return "";
  }
  if (days <= 0) {
    return "today";
  }
  if (days < 14) {
    return `in ${days} day${days === 1 ? "" : "s"}`;
  }
  if (days < 60) {
    return `in ${Math.round(days / 7)} weeks`;
  }
  return `in ${Math.round(days / 30)} months`;
}

function useNudges() {
  const [state, setState] = useState({ loading: FEATURES.nudges, nudges: [] });
  useEffect(() => {
    if (!FEATURES.nudges) {
      return undefined;
    }
    let cancelled = false;
    API.getNudges()
      .then((res) => {
        if (!cancelled) {
          // Keep answered ones too: a deep link (?nudge=) to an already-
          // answered nudge shows a receipt, idempotent, never an error (E §2).
          setState({ loading: false, nudges: Array.isArray(res.data) ? res.data : [] });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ loading: false, nudges: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

function initialsOf(name) {
  return (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function nudgeHeadline(nudge, name) {
  if (nudge.reason === "renewal") {
    return `${name}'s policy renewal is coming up around ${formatDate(nudge.keyDate)}.`;
  }
  return `It's been about a year since we quoted ${name}.`;
}

/**
 * The name for a nudge. GET /api/nudges returns prospectRef (an id) but not
 * always prospectName, so join to the scoped prospects we already have — the
 * name is the sentence's anchor (E integration P1). Durable populate can come
 * later; this works at both tiers off the book store.
 */
export function nudgeName(nudge, prospectsById) {
  if (nudge.prospectName) {
    return nudge.prospectName;
  }
  const ref = nudge.prospectRef && (nudge.prospectRef._id || nudge.prospectRef);
  const prospect = ref ? prospectsById.get(String(ref)) : null;
  return (prospect && prospect.name) || "A past referral";
}

function NudgeSection({ highlightId }) {
  const toast = useToast();
  const { loading, nudges } = useNudges();
  const { prospects } = useBookData();
  const prospectsById = useMemo(
    () => new Map((prospects || []).map((p) => [String(p._id), p])),
    [prospects]
  );
  const [expanded, setExpanded] = useState(false);
  const [settled, setSettled] = useState(0);

  const pending = nudges.filter((n) => n.status === "pending");
  // Deep link to an already-answered nudge → one-line receipt in its slot.
  const answeredTarget =
    highlightId && nudges.find((n) => n._id === highlightId && n.status !== "pending");

  if (!FEATURES.nudges || loading || (pending.length === 0 && !answeredTarget)) {
    return null; // absent, not empty (E §1.2)
  }

  const allDone = pending.length > 0 && settled >= pending.length;
  const visible = expanded ? pending : pending.slice(0, NUDGES_VISIBLE);
  const hiddenCount = pending.length - visible.length;

  return (
    <section className="feat-nudges">
      {allDone ? (
        <p className="feat-nudges__done">
          That's everyone — thank you. We'll take it from here. 🌿
        </p>
      ) : (
        <>
          <h2 className="feat-nudges__title">Worth a hello?</h2>
          <p className="feat-nudges__sub">A few of your past referrals have key dates coming up.</p>
          <div className="feat-nudges__stack">
            {answeredTarget ? (
              <NudgeCard
                nudge={{ _id: answeredTarget._id }}
                receipt={`You answered this one on ${formatDate(
                  answeredTarget.decidedAt || answeredTarget.created_at
                )} — we're on it.`}
                highlight
                onCommit={() => Promise.resolve()}
              />
            ) : null}
            {visible.map((nudge) => {
              const name = nudgeName(nudge, prospectsById);
              return (
                <NudgeCard
                  key={nudge._id}
                  nudge={{
                    _id: nudge._id,
                    prospectName: name,
                    headline: nudgeHeadline(nudge, name),
                    context: nudge.context,
                    initials: initialsOf(name)
                  }}
                  highlight={highlightId === nudge._id}
                  onCommit={(status, note) =>
                    API.respondToNudge(nudge._id, { status, note: note || undefined }).then(() =>
                      setSettled((n) => n + 1)
                    )
                  }
                  onError={(msg) => toast.error(msg)}
                />
              );
            })}
          </div>
          {hiddenCount > 0 ? (
            <Button variant="ghost" onClick={() => setExpanded(true)}>
              Show {hiddenCount} more
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { identity, user } = useAuth();
  const { loading, households, prospects } = useBookData();

  const firstName =
    (identity && identity.firstName) || (user && user.email ? user.email.split("@")[0] : "there");

  const book = useMemo(() => {
    const activePolicies = households.flatMap((h) => h.activePolicies);
    const { total: premium } = sumPremiums(activePolicies);
    const commission = activePolicies.reduce((sum, p) => {
      const split = ownSplitFor(p, identity);
      const n = parsePremium(p.premium);
      return split == null || n >= PREMIUM_SANITY_CAP
        ? sum
        : sum + n * COMMISSION_RATE * (split / 100);
    }, 0);
    const activeHouseholds = households.filter((h) => h.activePolicies.length > 0).length;
    return {
      households: households.length,
      activeHouseholds,
      policies: activePolicies.length,
      premium,
      commission
    };
  }, [households, identity]);

  const openProspects = useMemo(
    () => prospects.filter((p) => !["bound", "lost"].includes(p.pipelineStage || "new_referral")),
    [prospects]
  );

  const radar = useMemo(() => {
    const rows = [];
    for (const h of households) {
      for (const policy of h.activePolicies) {
        const renewal = renewalInfo(policy);
        if (!renewal) {
          continue;
        }
        const days = daysUntil(renewal.date);
        if (days != null && days >= 0) {
          rows.push({ household: h, policy, renewal, days });
        }
      }
    }
    rows.sort((a, b) => a.days - b.days);
    const inWindow = rows.filter((r) => r.days <= RADAR_WINDOW_DAYS);
    // E §1.4: fall back to 90 days when the 60-day radar has <2 rows.
    const chosen =
      inWindow.length >= 2 ? inWindow : rows.filter((r) => r.days <= RADAR_FALLBACK_DAYS);
    return chosen.slice(0, 5);
  }, [households]);

  const glance = useMemo(() => {
    const counts = new Map();
    for (const p of openProspects) {
      const info = prospectStageInfo(p.pipelineStage);
      counts.set(info.shortLabel, (counts.get(info.shortLabel) || 0) + 1);
    }
    return Array.from(counts.entries());
  }, [openProspects]);

  const shareLink = identity
    ? `${window.location.origin}/quote?ref=${identity._id}`
    : `${window.location.origin}/quote`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied — anyone who quotes through it is attributed to you.");
    } catch (e) {
      toast.error("Couldn't copy the link — select and copy it directly.");
    }
  };

  const firstRun = !loading && households.length === 0 && openProspects.length === 0;

  return (
    <div className="shell-page feat-home">
      <header className="feat-home__greeting">
        <h1 className="shell-page__title">
          {greeting()}, {firstName}.
        </h1>
        {!loading && !firstRun ? (
          <p className="feat-home__summary">
            {book.households} household{book.households === 1 ? "" : "s"} ·{" "}
            {formatMoney(book.premium)} premium in force · ~{formatMoney(book.commission)} est.
            commission this year.
          </p>
        ) : null}
      </header>

      {loading ? (
        <SkeletonText lines={6} />
      ) : firstRun ? (
        <>
          <Card>
            <EmptyState
              title="Your book starts with one hello."
              message="Refer your first household and we'll handle the quoting."
              action={
                <Button
                  variant="primary"
                  icon={<PlusIcon size={14} />}
                  onClick={() => navigate("/app/refer")}
                >
                  Refer a household
                </Button>
              }
            />
          </Card>
          <ShareLinkCard link={shareLink} onCopy={copyShareLink} />
        </>
      ) : (
        <>
          <NudgeSection highlightId={searchParams.get("nudge")} />

          <div className="feat-home__stats">
            <StatTile
              label="Households"
              value={book.households}
              sub={`${book.activeHouseholds} with active policies`}
            />
            <StatTile
              label="Premium in force"
              value={formatMoney(book.premium)}
              sub={`${book.policies} polic${book.policies === 1 ? "y" : "ies"}`}
            />
            <StatTile
              label="Est. commission YTD"
              value={`~${formatMoney(book.commission)}`}
              sub="estimates from current premium"
            />
          </div>

          <Card title="Renewing soon" action={<Link to="/app/book?renews=60">All renewals →</Link>}>
            {radar.length === 0 ? (
              <EmptyState
                icon={<ClockIcon size={32} />}
                title="Nothing renewing soon"
                message={`Nothing renewing in the next ${RADAR_WINDOW_DAYS} days. Your book is quiet — a good thing.`}
              />
            ) : (
              <div className="feat-feed">
                {radar.map(({ household, policy, renewal, days }) => (
                  <button
                    key={policy._id}
                    type="button"
                    className="feat-feed__row"
                    onClick={() => navigate(`/app/clients/${household.id}`)}
                  >
                    <LobBadge lobKey={policy.lobKey} lob={policy.lob} />
                    <span className="feat-feed__main">
                      <span className="feat-feed__title">{household.name}</span>
                      <span className="feat-feed__meta">
                        {policy.carrier || "Unknown carrier"} · renews {formatDate(renewal.date)} (
                        {relativeDate(days)}) · {formatMoney(policy.premium)}/yr
                      </span>
                    </span>
                    {radarVariant(days) === "warning" ? (
                      <Badge variant="warning">{days}d</Badge>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card
            title="Your referrals in motion"
            action={<Link to="/app/pipeline">Pipeline →</Link>}
          >
            {openProspects.length === 0 ? (
              <EmptyState
                title="No open referrals right now."
                message="When you refer a household, its progress shows here."
                action={
                  <Button variant="secondary" onClick={() => navigate("/app/refer")}>
                    Refer a household
                  </Button>
                }
              />
            ) : (
              <>
                <div className="feat-badges" style={{ marginBottom: "var(--space-3)" }}>
                  {glance.map(([label, count]) => (
                    <Badge key={label} variant="neutral">
                      {label} {count}
                    </Badge>
                  ))}
                </div>
                <MiniTimeline
                  steps={[
                    { label: "Gathering info", done: true },
                    {
                      label: "Quoting",
                      done: openProspects.some((p) =>
                        ["quoting", "quotes_completed", "quotes_presented"].includes(
                          p.pipelineStage
                        )
                      )
                    },
                    {
                      label: "Presented",
                      done: openProspects.some((p) => p.pipelineStage === "quotes_presented")
                    }
                  ]}
                />
              </>
            )}
          </Card>

          <ShareLinkCard link={shareLink} onCopy={copyShareLink} />
        </>
      )}
    </div>
  );
}

function ShareLinkCard({ link, onCopy }) {
  return (
    <Card title="Your personal quote link">
      <div className="feat-sharelink">
        <span className="feat-sharelink__url">{link}</span>
        <Button variant="secondary" onClick={onCopy}>
          Copy
        </Button>
      </div>
      <p className="feat-cell-secondary" style={{ margin: "var(--space-2) 0 0" }}>
        Anyone who asks for a quote through your link is attributed to you.
      </p>
    </Card>
  );
}

export default HomePage;
